import * as React from 'react';
import { connect } from 'react-redux';

import { hasPayments, isAccepted } from 'utils/subcampaign';
import { isInfluencerProfileRejected } from 'utils/profile';
import { __t, applyTranslations } from 'utils/language';

import PageHeading from 'components/page/page-heading/PageHeading';
import NotFound from 'components/not-found/NotFound';
import CampaignBlank from '../../campaigns/components/CampaignBlank';
import SubcampaignPayouts from './payments/payouts/SubcampaignPayouts';

import { fetchSubcampaignPayments, resetSubcampaignPayments } from 'modules/subcampaigns/reducers/subcampaignPayments';
import { fetchVariablePayments, resetVariablePayments } from 'modules/subcampaigns/reducers/variable-payments/variable-payments';

import type { Payment, Subcampaign } from 'types';

applyTranslations(require('./subcampaign-payments.i18n.json'));

type StateProps = {
  subcampaign: Subcampaign,
  subcampaignPayments: Payment,
  variablePaymentsAmount: number
};

type DispatchProps = {
  dispatchFetchVariablePayments: (b: number, a: number) => Promise<void>,
  dispatchResetVariablePayments: () => Promise<void>,
  dispatchFetchSubcampaignPayments: (a: number) => Promise<void>,
  dispatchResetSubcampaignPayments: () => Promise<void>
};

type Props = StateProps & DispatchProps;

class SubcampaignPayments extends React.Component<Props> {
  componentDidMount() {
    const { dispatchFetchVariablePayments, dispatchFetchSubcampaignPayments, subcampaign } = this.props;
    const campaign = subcampaign.campaign;

    dispatchFetchSubcampaignPayments(subcampaign.id);
    dispatchFetchVariablePayments(campaign.id, subcampaign.id);
  }

  componentWillUnmount() {
    const { dispatchResetSubcampaignPayments, dispatchResetVariablePayments } = this.props;

    dispatchResetSubcampaignPayments();
    dispatchResetVariablePayments();
  }

  render() {
    const { subcampaign, subcampaignPayments, variablePaymentsAmount } = this.props;

    if (!subcampaign || !subcampaign.id) {
      // during loading state, just in case :)
      return null;
    }

    const campaign = subcampaign.campaign;
    const isInfluencerRejected = isInfluencerProfileRejected(subcampaign.creatorProfile);

    if (!isAccepted(subcampaign)) {
      return (
        <NotFound />
      );
    }

    if (!hasPayments(subcampaignPayments, variablePaymentsAmount)) {
      return (
        <CampaignBlank>
          {__t('subcampaign_payments.blank_message')}
        </CampaignBlank>
      );
    }

    return (
      <section>
        <PageHeading
          heading={__t('subcampaign_payments.section_payout_title')}
          message={
            isInfluencerRejected
              ? __t('subcampaign_payments.section_payouts_message_rejected')
              : __t('subcampaign_payments.section_payouts_message')
          }
          color="light"
          weight="bold"
          size="medium"
        />
        <SubcampaignPayouts
          campaign={campaign}
          subcampaign={subcampaign}
        />
      </section>
    );
  }
}

const mapStateToProps = ({ subcampaign, subcampaignPayments, subcampaignVariablePayments }): StateProps => ({
  subcampaign: subcampaign.data,
  subcampaignPayments: subcampaignPayments.payments,
  variablePaymentsAmount: subcampaignVariablePayments.variablePayments.totalAmount || 0
});

const mapDispatchToProps = (dispatch): DispatchProps => ({
  dispatchFetchVariablePayments: (campaignId: number, subcampaignId: number) => dispatch(fetchVariablePayments(campaignId, subcampaignId)),
  dispatchResetVariablePayments: () => dispatch(resetVariablePayments()),
  dispatchFetchSubcampaignPayments: (subcampaignId: number) => dispatch(fetchSubcampaignPayments(subcampaignId)),
  dispatchResetSubcampaignPayments: () => dispatch(resetSubcampaignPayments())
});

export default connect(mapStateToProps, mapDispatchToProps)(SubcampaignPayments);
